import { LIST_URL_SPECS } from '@pmp/utils';

import type { AppliedListFilter, ListController, ListFilterInstruction, ReadListController } from './listActions';
import {
  applyListFilter,
  describeApplied,
  holdListFilter,
  holdListOffer,
  isSafeListPath,
  listPathname,
  takeOpenListOffer,
  withOfferedFilters,
  OPEN_LIST_ACTION,
  OPEN_LIST_PARAMETERS,
} from './listActions';
import type { AssistantAction } from '@ohpen/enso-ui';

export { clearPendingListFilter, drainListFilter, holdListFilter, PENDING_INTENT_TTL_MS, takeListFilter } from './listActions';

// Which list is on screen right now, so the agent's instruction reaches the grid the user is looking at.
//
// Not a store: nothing renders from this, and the transport reads it outside React (agentTransport is a
// module-level function, so it cannot use a hook). Each list registers its own enso table ref while it is
// mounted and clears it on the way out, exactly as it already reports itself to the page context.
//
// An instruction for a list that is not mounted waits in `listActions`, and the list drains it as it comes up.

/**
 * What became of an instruction. The two ways nothing happened are told apart, because the panel says them
 * to the user in different words: a list that is not open is not the same as one that is open and has none
 * of the columns the agent named.
 */
export type ListFilterOutcome =
  | { status: 'applied'; applied: AppliedListFilter }
  | { status: 'navigating' }
  | { status: 'needs-confirmation' }
  | { status: 'unknown-columns'; dropped: string[] }
  | { status: 'not-open' };

const controllers = new Map<string, ReadListController>();

let navigateTo: ((path: string) => void) | null = null;

/** Registered by the assistant gate, which is inside the router and can navigate. */
export const registerListNavigator = (navigate: ((path: string) => void) | null): void => {
  navigateTo = navigate;
};

export const registerListController = (entityType: string, read: ReadListController): void => {
  controllers.set(entityType, read);
};

export const unregisterListController = (entityType: string, read: ReadListController): void => {
  // Only when it is still this list's own reader: on a route change the next list can register before the
  // previous one unmounts, and clearing then would leave the page with no reachable grid.
  if (controllers.get(entityType) === read) controllers.delete(entityType);
};

// Read through the list's own reader, so what comes back is the grid as it is now rather than as it was when
// the list registered.
export const findListController = (entityType: string): ListController | undefined => controllers.get(entityType)?.() ?? undefined;

// The path the agent sent, only when it is this app's own path for the list the instruction names. The
// registry, not the instruction, decides where a list lives; the query string is left as sent, since the
// tab and the page params are the part of it a list reads.
const navigablePath = (instruction: ListFilterInstruction): string | null => {
  const spec = LIST_URL_SPECS[instruction.entity];
  const path = instruction.path;
  if (spec === undefined || path === undefined || !isSafeListPath(path)) return null;

  return listPathname(path) === spec.route ? path : null;
};

/**
 * Filters the list the instruction names: straight away when it is on screen, and otherwise by navigating to
 * it and letting it apply the filter as it mounts. With the filter no longer in the query string, arriving at
 * a page and filtering it are two steps, and this is what keeps them one gesture for the user.
 *
 * The navigation half only ever runs for an instruction the user has agreed to: taking someone off the page
 * they are working on is not a side effect of answering their question, so an instruction that would move
 * them and does not say they agreed is refused here rather than obeyed. The agent asks first and sends it
 * again once they say yes.
 *
 * Says which of the five things happened, so the step the panel paints is the one the user can check against
 * their own screen.
 */
export const runListFilter = (instruction: ListFilterInstruction): ListFilterOutcome => {
  // An agreement is to open what was offered, so it inherits the offer's filters when it names none of its own.
  const wanted = instruction.openList === true ? withOfferedFilters(instruction) : instruction;

  const controller = findListController(wanted.entity);
  if (controller !== undefined) {
    const applied = applyListFilter(wanted, controller);
    return applied === null
      ? { status: 'unknown-columns', dropped: Object.keys(wanted.filters ?? {}) }
      : { status: 'applied', applied };
  }

  const path = navigablePath(wanted);
  if (path === null || navigateTo === null) return { status: 'not-open' };
  if (wanted.openList !== true) {
    holdListOffer(wanted);
    return { status: 'needs-confirmation' };
  }

  holdListFilter(wanted);
  navigateTo(path);
  return { status: 'navigating' };
};

/**
 * The action behind the panel's confirm affordance: the user agreeing to be taken to a list they do not have
 * open. Registered on the assistant config, so enso offers it on the step itself and runs it when they click.
 *
 * `confirm` is enso's default, and is set here anyway because the whole point of this action is that it is
 * never taken without being agreed to.
 */
export const openFilteredListAction: AssistantAction = {
  name: OPEN_LIST_ACTION,
  description: 'Open one of the portal lists, filtered to the rows the answer describes. Offered to the user, never taken on its own.',
  parameters: OPEN_LIST_PARAMETERS,
  confirm: 'confirm',
  execute: params => {
    const instruction = takeOpenListOffer(params);
    if (instruction === null) return 'That offer is no longer available.';

    const outcome = runListFilter(instruction);
    switch (outcome.status) {
      case 'applied':
        return describeApplied(outcome.applied, instruction.list);
      case 'navigating':
        return `Opening ${instruction.list ?? instruction.entity}`;
      case 'unknown-columns':
        return `${instruction.list ?? instruction.entity} has no ${outcome.dropped.join(', ')}, so nothing was filtered`;
      default:
        return `${instruction.list ?? instruction.entity} could not be opened`;
    }
  },
  serializeResult: result => String(result),
};
